/**
 * Kane SSO — `return_to` allowlist validator (#385).
 *
 * SP-initiated SSO: Kane OneDeck sends the user to ProMedic's `/kane-launch`
 * route with a `return_to` query param. After ProMedic mints a handoff token,
 * it redirects the browser to `${return_to}#token=<jwt>`.
 *
 * `return_to` is attacker-controllable (it comes in the URL), so it MUST be
 * validated against a strict allowlist before we ever forward a token to it —
 * otherwise this is an open-redirect that leaks the SSO JWT to any origin.
 *
 * Rules enforced here:
 *   1. Must parse as an http(s) URL.
 *   2. Its ORIGIN must be an allowlisted OneDeck origin.
 *   3. Its PATH must be exactly `/sso` (OneDeck's handoff-exchange route).
 *   4. The returned target is reconstructed from origin + path only — any
 *      caller-supplied query or fragment is dropped so nothing can be smuggled
 *      alongside the token.
 *
 * A non-matching value returns `null` → the caller rejects and never mints a
 * token.
 */

// Kane OneDeck production origin.
const PROD_ORIGINS = ['https://kane-onedeck.vercel.app'];

// Local dev origins. OneDeck's Vite dev server is pinned to :5000
// (vite.config → server.port 5000, strictPort). :4173 is Vite's preview port.
const DEV_ORIGINS = ['http://localhost:5000', 'http://localhost:4173'];

// OneDeck's token-exchange route (SsoHandoff.tsx lives at /sso).
const ALLOWED_PATH = '/sso';

/**
 * Build the effective allowlist.
 *
 * In production (REACT_APP_ENV === 'production') only prod origins are allowed
 * by default. In any other env we also allow the localhost dev origins so the
 * flow is testable end-to-end locally.
 *
 * `REACT_APP_KANE_ALLOWED_ORIGINS` (comma-separated) can add extra origins in
 * any env — use it to whitelist OneDeck Vercel *preview* deployments without a
 * code change.
 */
export function getAllowedReturnOrigins() {
  const extra = (process.env.REACT_APP_KANE_ALLOWED_ORIGINS || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const base =
    process.env.REACT_APP_ENV === 'production'
      ? PROD_ORIGINS
      : [...PROD_ORIGINS, ...DEV_ORIGINS];

  return Array.from(new Set([...base, ...extra]));
}

/**
 * Validate a `return_to` value.
 *
 * @param {unknown} returnTo raw query-param value
 * @returns {string|null} a clean `${origin}${path}` string safe to redirect to,
 *   or `null` when the value is missing/invalid/not allowlisted.
 */
export function validateReturnTo(returnTo) {
  if (!returnTo || typeof returnTo !== 'string') return null;

  let url;
  try {
    url = new URL(returnTo);
  } catch {
    return null;
  }

  // Reject anything that isn't plain http(s) (blocks javascript:, data:, etc.
  // whose URL.origin is the string "null" and would never match anyway, but be
  // explicit).
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  if (!getAllowedReturnOrigins().includes(url.origin)) return null;

  if (url.pathname !== ALLOWED_PATH) return null;

  // Reconstruct from origin + path only — drop caller query/hash.
  return `${url.origin}${url.pathname}`;
}
